/**
 * Deterministic USD formatting — the single source of truth for spend display.
 *
 * WHY THIS EXISTS. Two surfaces formatted money divergently: the Usage page used
 * a hand-rolled `$`-prefix string, while Provider Credits used
 * `toLocaleString(undefined, { style: "currency" })` — which is LOCALE-dependent
 * (it renders `US$1.23` or `1,23 $` depending on the runtime/region) and so
 * violates the same "format identically across every runtime" doctrine that
 * `format-date.ts` enforces for dates. This helper is the one deterministic
 * implementation; both surfaces call it.
 */

/**
 * Format a USD amount for display. Explicit rounding policy:
 *   - zero → `$0.00`
 *   - sub-cent magnitude (|v| < 0.01) → 4 decimals so tiny per-call costs are visible
 *   - otherwise → 2 decimals (cents)
 * Negative amounts (credits / adjustments) render as `-$0.50`, with the sign
 * OUTSIDE the `$` (not `$-0.50`). Non-finite input clamps to `$0.00`.
 */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return "$0.00"
  const negative = value < 0
  const abs = Math.abs(value)
  const body = abs === 0 ? "0.00" : abs < 0.01 ? abs.toFixed(4) : abs.toFixed(2)
  return `${negative ? "-" : ""}$${body}`
}
